import { Resources } from 'constants/resources';

import { useCallback } from 'react';

import { generateName } from 'config/requests';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useInput } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { IconButton } from 'shared/mui/IconButtons';
import { GenerateTextIconStyled } from 'shared/mui/Icons';
import { Tooltip } from 'shared/mui/Tooltip/Tooltip/Tooltip';
import { Typography } from 'shared/mui/Typography';
import { TextInput, TextInputProps } from 'shared/react-admin/Inputs';

type Props = {
  merchantId?: string;
} & TextInputProps;

export const CommissionsNameInput: React.FC<Props> = ({ merchantId, readOnly, ...props }) => {
  const translate = useTypedTranslate();

  const { field } = useInput({ source: props.source });
  const [provider, currency, merchant, transactionTypes] = useWatch({
    name: ['provider', 'currency', 'merchant', 'transactionTypes'],
  });

  const isFormInvalid = !provider || !merchant || !currency || !transactionTypes?.length;

  const handleClick = useCallback(async () => {
    const response = await generateName(Resources.Commissions, {
      merchant: merchantId || merchant,
      provider,
      currency,
      transactionTypes,
    });
    field.onChange(response.data.name);
  }, [merchant, provider, currency, transactionTypes]);

  const missingFields = [
    !merchant && translate('app.table.merchant'),
    !provider && translate('app.table.provider'),
    !currency && translate('app.table.currency'),
    !transactionTypes?.length && translate('app.commissions.transactionTypes'),
  ].filter(Boolean);

  const invalidFormMessage = `. ${translate(
    'app.merchantCommissions.fieldsRequiredForGeneration',
  )}: ${missingFields.join(', ')}.`;

  return (
    <TextInput
      {...props}
      InputProps={{
        readOnly,
        endAdornment: (
          <Tooltip
            arrow
            placement="top"
            title={
              <Typography fontSize={12}>
                {translate('app.merchantCommissions.generateName')}
                {isFormInvalid && invalidFormMessage}
              </Typography>
            }
          >
            <span>
              <IconButton disabled={isFormInvalid || readOnly} onClick={handleClick}>
                <GenerateTextIconStyled disabled={isFormInvalid} />
              </IconButton>
            </span>
          </Tooltip>
        ),
      }}
      size="small"
    />
  );
};
